<template>
  <article
    class="product-card"
    :class="{
      'product-card--no-specs': !showParams,
    }"
    role="button"
    @click="showMoreInfo"
  >
    <div class="product-card__badges">
      <CardBadge v-if="isSpecialOffer" :text="$t('Special offer')">
        <svg
          width="14"
          height="14"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
          class="product-card__badge-icon"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="m2.17178 11.4108-.80902-.4806c-.35825-.2127-.57989-.5967-.585092-1.01333L.766056 8.976C.763553 8.7761.711001 8.58.613201 8.40572L.15293 7.5851c-.2039068-.36349-.2039068-.80672 0-1.1702l.460271-.82062c.0978-.17428.150354-.37038.152855-.57029l.011612-.94086c.005205-.41663.226832-.80061.585092-1.01333l.80902-.4806c.17187-.1021.31533-.24555.41742-.41742l.4806-.80902c.21271-.35825.5967-.57989 1.01333-.585092l.94086-.011612c.19991-.002503.396-.055055.57029-.152855L6.4149.15293c.36349-.2039068.80671-.2039068 1.1702 0l.82062.460271c.17428.0978.37038.150354.57029.152855l.94086.011612c.41663.005205.80063.226832 1.01333.585092l.4806.80902c.1021.17187.2455.31533.4174.41742l.809.4806c.3583.21271.5799.5967.5851 1.01333l.0116.94086c.0025.19991.0551.396.1529.57029l.4603.82062c.2039.36348.2039.80671 0 1.1702l-.4603.82062c-.0978.17428-.1504.37038-.1529.57028l-.0116.94087c-.0052.41663-.2268.80063-.5851 1.01333l-.809.4806c-.1719.1021-.3153.2455-.4174.4174l-.4806.809c-.2127.3583-.5967.5799-1.01333.5851l-.94086.0116c-.19991.0025-.396.0551-.57029.1529l-.82062.4603c-.36349.2039-.80671.2039-1.1702 0l-.82062-.4603c-.17428-.0978-.37038-.1504-.57029-.1529l-.94086-.0116c-.41663-.0052-.80061-.2268-1.01333-.5851l-.4806-.809c-.1021-.1719-.24555-.3153-.41742-.4174Zm2.44809-2.50486c-.21221.25296-.17928.63005.07368.84236.25305.21232.63014.17928.84245-.07368l3.84341-4.58067c.21222-.25295.17929-.63004-.07377-.84235-.25296-.21232-.63005-.17929-.84236.07367L4.61987 8.90594ZM5.7313 5.73163c.46699-.46708.46699-1.22423 0-1.69122-.46709-.46709-1.22424-.46709-1.69132 0-.46699.46699-.46699 1.22416 0 1.69122.46708.46699 1.22423.46699 1.69132 0Zm4.22806 4.22806c.46704-.46699.46704-1.22416 0-1.69122-.46699-.46699-1.22416-.46699-1.69123 0-.46698.46708-.46698 1.22423 0 1.69122.46709.46711 1.22424.46711 1.69123 0Z"
          />
        </svg>
      </CardBadge>

      <CardBadge v-if="hasVAT" :text="$t('VAT refundable')" color="blue" />
    </div>

    <div class="product-card__preview">
      <div ref="swiper" class="swiper product-card-slider">
        <div class="swiper-wrapper">
          <div
            v-for="image in preparedImages"
            :key="image.id"
            class="swiper-slide product-card-slider__slide"
            itemscope
            itemtype="http://schema.org/ImageObject"
          >
            <img
              :src="image.uri"
              :alt="image.alt || ''"
              :title="image.title || ''"
              class="product-card-slider__image"
              itemprop="contentUrl"
            />
            <span class="visually-hidden" itemprop="name">
              {{ productName }}
            </span>
            <span class="visually-hidden" itemprop="description">
              {{ image.title }}
            </span>
          </div>
        </div>

        <div class="swiper-pagination"></div>

        <template v-if="preparedImages.length > 1">
          <div class="swiper-button-prev" @click.stop>
            <i class="icon icon-chevron-left"></i>
          </div>

          <div class="swiper-button-next" @click.stop>
            <i class="icon icon-chevron-right"></i>
          </div>
        </template>
      </div>
    </div>

    <div class="product-card__body">
      <div class="product-card__info product-card-info">
        <p class="product-card-info__name">
          {{ productName }}
        </p>

        <ul v-if="showParams" class="product-card-info__specs product-specs">
          <li v-if="isPcsOrPkg(product.wt_unit)" class="product-specs__item">
            <span> {{ $t('Weight of one {0}', [$t(`units.weight.${product.wt_unit.name}`)]) }}. </span>
            <span>{{ product.one_item_weight }} {{ $t('kg') }}</span>
          </li>

          <li v-else class="product-specs__item">
            <span>{{ $t('Packing') }}</span>
            <span>{{ product.packing.name }}</span>
          </li>

          <li class="product-specs__item">
            <span>{{ $t('Country') }}</span>
            <span>{{ product.country.name }}</span>
          </li>

          <li class="product-specs__item">
            <span>{{ $t('Quality class') }}</span>
            <span>{{ product.category.name }}</span>
          </li>
        </ul>
      </div>

      <div class="product-card__prices">
        <div class="product-card__price-wrapper">
          <p
            v-b-popover.hover.right="$t('Pickup price')"
            class="product-card__price"
            :class="{ 'product-card__price--special': isSpecialOffer }"
          >
            {{ formatPrice(product.price, product.price_unit_text) }}/{{ $t(`units.weight.${product.wt_unit.name}`) }}
          </p>

          <p v-if="hasDiscount" class="product-card__discount-info">
            <span>
              {{ formatPrice(product.full_price) }}
            </span>
            <span>(-{{ product.discount }}%)</span>
          </p>
        </div>

        <button
          v-if="canAddToCart"
          class="product-card__add-to-cart"
          @click.stop="isProductInCart ? goToCart() : addToCart()"
        >
          <Check v-if="isProductInCart" class="icon-cart" />
          <i v-else class="icon-cart" />
        </button>
      </div>

      <div class="product-card__availability">
        <span class="product-card__from">
          {{ $t('marketQuantityFrom', [product.weight, weightUnitText]) }}
        </span>

        <span
          :class="{
            'product-card__status': true,
            'product-card__status--available': hasSystemSeller,
            'product-card__status--unavailable': isExpired || isOutOfStock,
          }"
        >
          <span v-if="isExpired || isOutOfStock">{{ $t('Not available') }}</span>
          <span v-else>{{ hasSystemSeller ? $t('Available') : $t('Preorder') }}</span>
        </span>
      </div>

      <footer class="product-card__footer">
        <div class="product-card__person">
          <router-link
            class="product-card__owner"
            :to="{ name: 'provider', params: { userId: productSellerInfo.id, scrollTop: true } }"
            @click.native.stop
          >
            <b-avatar :src="productSellerInfo.avatar" class="product-card__owner-avatar"></b-avatar>

            <div class="product-card__owner-details">
              <p class="product-card__owner-name">{{ productSellerInfo.name }}</p>

              <div class="product-card__company-name">
                <span>
                  {{ productSellerInfo.organization.name }}
                </span>
                <img
                  v-if="productSellerInfo.organization.verified"
                  class="product-card__company-checked"
                  src="@/assets/images/icons/checked.svg"
                  width="12"
                  height="12"
                  alt="verified"
                  :title="$t('Organization verified')"
                />
              </div>
            </div>
          </router-link>

          <span class="product-card__location">{{ productLocation.cityName }}</span>
        </div>
      </footer>
    </div>
  </article>
</template>

<script>
import productCard from '@/mixins/product/productCard'
import CardBadge from '@/components/common/CardBadge.vue'
import Swiper, { Navigation, Pagination } from 'swiper'
import 'swiper/css'
import 'swiper/css/pagination'
import { mapGetters } from 'vuex'
import { Check } from 'lucide-vue'

export default {
  name: 'ProductCard',
  components: {
    CardBadge,
    Check,
  },
  mixins: [productCard],
  computed: {
    ...mapGetters('misc', ['isPcsOrPkg']),
    weightUnitText() {
      return this.$t('units.weight.' + this.product.wt_unit.name)
    },
    productCategoryId() {
      return this.product.category ? this.product.category.id : 0
    },
    hasDiscount() {
      return this.product.discount > 0
    },
  },
  mounted() {
    new Swiper(this.$refs.swiper, {
      modules: [Pagination, Navigation],
      loop: true,
      slidesPerView: 1,
      pagination: {
        el: '.swiper-pagination',
      },
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      },
    })
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';

.swiper-button-prev,
.swiper-button-next {
  --buttons-gap: 4px;
  --buttons-width: 18px;
  --buttons-offset: 8px;

  position: absolute;
  bottom: 0;
  transform: translateY(-50%);
  width: var(--buttons-width);
  height: var(--buttons-width);
  background-color: $GreenM;
  border-radius: 50%;
  color: $White;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  .icon {
    font-size: calc(var(--buttons-width) / 2);
  }
}

.swiper-button-prev {
  right: calc(var(--buttons-offset) + var(--buttons-gap) + var(--buttons-width));
}

.swiper-button-next {
  right: var(--buttons-offset);
}

.swiper-pagination-bullet {
  --size: 6px;

  width: var(--size);
  height: var(--size);
  margin: 0 2px !important;
}

@media (min-width: 992px) {
  .swiper-button-next,
  .swiper-button-prev {
    --buttons-width: 24px;
  }
}
</style>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';

.product-card {
  position: relative;
  transition: $transition;
  background-color: $White;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;

  --gallery-height: 130px;

  @media (min-width: 359px) {
    --gallery-height: 140px;
  }

  @include media-breakpoint-up(md) {
    --gallery-height: 160px;
  }

  @include media-breakpoint-up(lg) {
    --gallery-height: 180px;
  }

  @include media-breakpoint-up(xl) {
    --gallery-height: 190px;
  }

  &--no-specs {
    @media (min-width: 359px) {
      --gallery-height: 130px;
    }

    @include media-breakpoint-up(md) {
      --gallery-height: 130px;
    }

    @include media-breakpoint-up(lg) {
      --gallery-height: 140px;
    }

    @include media-breakpoint-up(xl) {
      --gallery-height: 150px;
    }
  }

  &:hover .product-card__preview img {
    transform: scale(1.05);
  }

  &__body {
    display: flex;
    flex-direction: column;
    padding: 8px;
    flex: 1;

    @include media-breakpoint-up(lg) {
      padding: 8px 12px;
    }
  }

  &__preview {
    width: 100%;
    min-height: var(--gallery-height);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 0;

    img {
      transition: transform 200ms ease;
    }
  }

  .product-card-slider {
    height: 100%;
    width: 100%;

    &__slide {
      width: 100% !important;
      height: var(--gallery-height);
    }

    &__image {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .swiper-pagination {
      bottom: 0;
    }
  }

  &__additional {
    display: none;
  }

  &__badges {
    position: absolute;
    left: 4px;
    top: 4px;
    z-index: 10;
    display: grid;
    justify-items: flex-start;
    gap: 4px;

    @include media-breakpoint-up(lg) {
      left: 8px;
      top: 8px;
    }
  }

  &__badge-icon {
    transform: scale(0.7);

    @include media-breakpoint-up(md) {
      transform: scale(0.85);
      margin-right: 2px;
    }
  }

  &__prices {
    min-height: max-content;
    padding-top: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    line-height: 1.2;
    flex: 1;

    @include media-breakpoint-up(lg) {
      padding-top: 12px;
    }
  }

  &__price-wrapper {
    display: flex;
    flex-direction: column;
  }

  &__price {
    font-size: 16px;
    line-height: 1.17;
    font-weight: 600;

    &--special {
      color: $BrightRed;
    }

    @include media-breakpoint-up(lg) {
      font-size: 18px;
      line-height: 20px;
    }
  }

  &__discount-info {
    display: inline-flex;
    align-items: center;
    color: $DGray;
    font-size: 12px;

    > span:first-child {
      text-decoration: line-through;
      margin-right: 4px;
    }

    > span:nth-child(2) {
      color: $PlainRed;
    }

    @include media-breakpoint-up(lg) {
      font-size: 14px;
    }
  }

  &__add-to-cart {
    --size: 32px;
    --icon-size: calc(var(--size) / 2);

    border: none;
    width: var(--size);
    height: var(--size);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $GreenM;
    border-radius: 50%;
    color: $White;

    .icon-cart {
      font-size: var(--icon-size);
      width: var(--icon-size);
      height: var(--icon-size);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &:hover {
      text-decoration: none;
    }

    @include media-breakpoint-up(lg) {
      --size: 36px;
    }
  }

  &__availability {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 4px;
    font-size: 11px;
    line-height: 1.1;

    @media (min-width: 359px) {
      font-size: 12px;
    }

    @include media-breakpoint-up(lg) {
      padding-top: 6px;
      font-size: 14px;
      line-height: 16px;
    }
  }

  &__status {
    color: $DGray;

    &--available {
      color: $GreenM;
    }

    &--unavailable {
      color: $LRed;
    }
  }

  &__from {
    font-weight: 500;
  }

  &__footer {
    --gap: 6px;

    padding-top: var(--gap);
    margin-top: var(--gap);
    border-top: 1px solid $LGray3;

    @include media-breakpoint-up(md) {
      --gap: 8px;
    }
  }

  &__person {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__owner {
    display: flex;
    align-items: center;
    color: inherit;
    text-decoration: none;
    margin-right: 8px;
    overflow: hidden;

    .b-avatar {
      --size: 24px;

      margin-right: 6px;
      width: var(--size);
      height: var(--size);

      @include media-breakpoint-up(lg) {
        --size: 34px;
      }
    }

    &-name {
      display: block;
      max-width: 100%;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  &__owner-details {
    font-size: 11px;
    line-height: 1.17;
    display: flex;
    flex-direction: column;

    @media (min-width: 359px) {
      font-size: 12px;
    }

    > p {
      font-weight: 500;
    }

    @include media-breakpoint-up(lg) {
      font-size: 14px;
    }
  }

  &__company-name {
    display: inline-flex;
    align-items: center;
    font-size: 11px;

    @media (min-width: 359px) {
      font-size: 12px;
    }

    > img {
      margin-left: 4px;
      width: 10px;
      height: 10px;
    }

    @include media-breakpoint-up(lg) {
      font-size: 14px;
    }
  }

  &__location {
    color: $DGray;
    margin-left: auto;
    font-size: 10px;
    line-height: 1.17;
    white-space: pre-line;
    text-align: right;

    @media (min-width: 359px) {
      font-size: 12px;
    }

    @include media-breakpoint-up(lg) {
      font-size: 14px;
    }
  }
}

.product-card-info {
  flex: 1;

  &__name {
    font-weight: 500;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    line-height: 1.17;

    @include media-breakpoint-up(lg) {
      font-size: 18px;
      line-height: 20px;
    }
  }

  &__specs {
    padding-top: 4px;
  }
}

.product-specs {
  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    color: $Dark100;
    padding: 2px 0;
    font-size: 11px;
    line-height: 1.17;

    @media (min-width: 359px) {
      font-size: 12px;
    }

    @include media-breakpoint-up(lg) {
      font-size: 14px;
      line-height: 16px;
      padding-bottom: 0;
      padding-top: 3px;
    }
  }
}
</style>
